import React, { useContext, useEffect, useRef, useState } from "react";
import "./EmployeeReportTool.css";
import reportsApi from "../services/reportsApi";
import BackButton from "../components/common/BackButton";
import {
    FaClock, FaInfoCircle,
    FaRocket, FaSave, FaChevronDown, FaCalendarAlt, FaGlobe,
    FaCogs, FaFileExcel, FaEnvelope, FaUserTag
} from "react-icons/fa";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import { useToast } from "../context/ToastContext";
import { AuthContext } from "../context/AuthContext";
import ToolInfoModal from "../components/common/ToolInfoModal";
import { hasAnyPermission } from "../utils/roleUtils";

const TIMEZONES = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "UTC",
];

const CATEGORY_INFO = {
    "Main Store Sales Staff": "Evaluated strictly on their individual DPH and individual UPT metrics.",
    "Main Store Assistant Managers": "Evaluated based on the entire store's combined DPH and combined UPT performance.",
    "Outlet Sales Staff": "Evaluated strictly on their individual DPH and individual UPT metrics.",
};

const defaultSchedule = {
    enabled: false,
    day_of_month: 1,
    hour: 7,
    minute: 0,
    timezone: "America/New_York",
    email_recipients: "",
    next_run_at: null,
};

const getPreviousMonthName = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const monthIndex = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    return new Date(year, monthIndex, 1).toLocaleString("en-US", { month: "long" });
};

const padNumber = (value) => String(value).padStart(2, "0");

const getOrdinalSuffix = (i) => {
    let j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
};

const CustomSelect = ({ options, value, onChange, disabled, icon: Icon, placeholder, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => String(o.value) === String(value)) || { label: placeholder || "Select..." };

    return (
        <div className={`ert-custom-select ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${className}`} ref={selectRef}>
            <div className="ert-select-trigger" onClick={() => !disabled && setIsOpen(!isOpen)}>
                <div className="ert-select-value">
                    {Icon && <Icon className="ert-select-icon" />}
                    <span>{selectedOption.label}</span>
                </div>
                <FaChevronDown className="ert-select-caret" />
            </div>
            {isOpen && !disabled && (
                <div className="ert-select-dropdown">
                    {options.map((opt) => (
                        <div
                            key={opt.value}
                            className={`ert-select-option ${String(value) === String(opt.value) ? 'selected' : ''}`}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

function EmployeeReportTool() {
    const [activeTab, setActiveTab] = useState("execution");
    const [schedule, setSchedule] = useState(defaultSchedule);
    const [config, setConfig] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(getPreviousMonthName());
    const [loadingSchedule, setLoadingSchedule] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const { addToast } = useToast();
    const { user } = useContext(AuthContext);
    const [showInfoModal, setShowInfoModal] = useState(false);

    const canRunReport = hasAnyPermission(user, ["reports.performance.generate", "reports.*"]);
    const canManageSchedule = hasAnyPermission(user, ["reports.performance.schedule", "reports.*"]);
    const canManageConfig = hasAnyPermission(user, ["reports.config.performance.employee", "reports.*"]);
    const showExecutionTab = canRunReport || canManageSchedule;
    const showSettingsTab = canManageConfig;

    const toolInfo = {
        toolName: "Employee Performance Report",
        description: "Configure automatic monthly performance reports or generate them on-demand.",
        steps: [
            "Use the 'Execution' tab to manually generate a report or modify schedule timing.",
            "Schedule allows entering comma-separated email addresses for automated distribution.",
            "Use the 'Settings' tab to update the DPH and UPT bonus threshold variables for all store groups.",
            "Click 'Save Configuration' after making changes to apply them immediately.",
        ],
        templates: [],
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManageConfig, canManageSchedule]);

    useEffect(() => {
        if (activeTab === "execution" && !showExecutionTab && showSettingsTab) {
            setActiveTab("settings");
            return;
        }

        if (activeTab === "settings" && !showSettingsTab && showExecutionTab) {
            setActiveTab("execution");
        }
    }, [activeTab, showExecutionTab, showSettingsTab]);

    const fetchData = async () => {
        setLoadingSchedule(true);


        let loadedSchedule = defaultSchedule;
        let loadedConfig = null;
        let errorMsg = "";
        const shouldLoadSchedule = canManageSchedule || canManageConfig;

        if (shouldLoadSchedule) {
            try {
                const scheduleRes = await reportsApi.get("/performance/schedule");
                loadedSchedule = scheduleRes.data.schedule || defaultSchedule;
                setCurrentMonth(scheduleRes.data.current_month || getPreviousMonthName());
            } catch (error) {
                errorMsg += "Schedule load failed. ";
            }
        } else {
            setCurrentMonth(getPreviousMonthName());
        }

        if (canManageConfig) {
            try {
                const configRes = await reportsApi.get("/config/employee");
                loadedConfig = configRes.data;
            } catch (error) {
                errorMsg += "Config load failed. ";
            }
        }

        if (errorMsg && canManageConfig) {
            // Provide a graceful fallback config so the UI does not blank out
            loadedConfig = loadedConfig || {
                parameters: {
                    "Main Store Sales Staff": {
                        "upt": { "start_threshold": 2.0, "step_size": 0.25, "base_bonus": 0.50, "bonus_per_step": 0.50 },
                        "dph": { "start_threshold": 270.0, "step_size": 50.0, "base_bonus": 1.00, "bonus_per_step": 1.00 }
                    },
                    "Main Store Assistant Managers": {
                        "upt": { "start_threshold": 2.0, "step_size": 0.25, "base_bonus": 0.50, "bonus_per_step": 0.50 },
                        "dph": { "start_threshold": 270.0, "step_size": 50.0, "base_bonus": 1.00, "bonus_per_step": 1.00 }
                    },
                    "Outlet Sales Staff": {
                        "upt": { "start_threshold": 1.5, "step_size": 0.25, "base_bonus": 1.00, "bonus_per_step": 0.50 },
                        "dph": { "start_threshold": 110.0, "step_size": 50.0, "base_bonus": 1.00, "bonus_per_step": 1.00 }
                    },
                    "Management": {
                        "upt": { "start_threshold": 0.0, "step_size": 1.0, "base_bonus": 0.0, "bonus_per_step": 0.0 },
                        "dph": { "start_threshold": 0.0, "step_size": 1.0, "base_bonus": 0.0, "bonus_per_step": 0.0 }
                    },
                    "Other": {
                        "upt": { "start_threshold": 0.0, "step_size": 1.0, "base_bonus": 0.0, "bonus_per_step": 0.0 },
                        "dph": { "start_threshold": 0.0, "step_size": 1.0, "base_bonus": 0.0, "bonus_per_step": 0.0 }
                    }
                }
            };
            addToast(`Using defaults. Could not load full stored data: ${errorMsg}`, "error");
        }

        setSchedule(loadedSchedule);
        setConfig(loadedConfig);
        setLoadingSchedule(false);
    };

    const handleScheduleChange = (key, value) => {
        setSchedule((prev) => ({ ...prev, [key]: value }));
    };

    const handleParameterChange = (category, metric, field, value) => {
        if (!config) return;
        setConfig(prevConfig => ({
            ...prevConfig,
            parameters: {
                ...prevConfig.parameters,
                [category]: {
                    ...prevConfig.parameters[category],
                    [metric]: {
                        ...prevConfig.parameters[category][metric],
                        [field]: Number(value)
                    }
                }
            }
        }));
    };

    const handleSaveSchedule = async () => {
        if (!canManageSchedule) {
            addToast("You do not have permission to update the report schedule.", "error");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                enabled: schedule.enabled,
                day_of_month: Number(schedule.day_of_month),
                hour: Number(schedule.hour),
                minute: Number(schedule.minute),
                timezone: schedule.timezone,
                email_recipients: schedule.email_recipients,
            };
            const response = await reportsApi.put("/performance/schedule", payload, {
                headers: { "Content-Type": "application/json" },
            });
            setSchedule(response.data.schedule || defaultSchedule);
            setCurrentMonth(response.data.current_month || getPreviousMonthName());
            addToast("Schedule configuration saved successfully.", "success");
        } catch (error) {
            const errorText = error?.response?.data?.error || "Failed to save schedule.";
            addToast(errorText, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveConfig = async () => {
        if (!canManageConfig) {
            addToast("You do not have permission to update report parameters.", "error");
            return;
        }

        setSavingConfig(true);

        try {
            // Include schedule so we don't drop it from the Mongo doc
            const payload = {
                ...config,
                schedule: {
                    ...schedule,
                    next_run_at: undefined // Do not save derived value
                }
            };
            const response = await reportsApi.put("/config/employee", payload, {
                headers: { "Content-Type": "application/json" },
            });
            setConfig(response.data.config);
            addToast("Parameter configuration saved successfully.", "success");
        } catch (error) {
            const errorText = error?.response?.data?.error || "Failed to save parameter configuration.";
            addToast(errorText, "error");
        } finally {
            setSavingConfig(false);
        }
    };

    const handleTriggerNow = async () => {
        if (!canRunReport) {
            addToast("You do not have permission to generate the employee report.", "error");
            return;
        }

        setTriggering(true);

        try {
            const response = await reportsApi.post("/performance/trigger", {}, { responseType: "blob" });

            if (
                response.data.type === "application/json" ||
                (response.headers["content-type"] && response.headers["content-type"].includes("application/json"))
            ) {
                const text = await response.data.text();
                const json = JSON.parse(text);
                addToast(json.error || "Failed to generate report.", "error");
                return;
            }

            const blob = new Blob([response.data], {
                type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const contentDisposition = response.headers["content-disposition"];
            const extractedFilename = extractFilenameFromHeader(contentDisposition);
            link.setAttribute("download", extractedFilename || "Employee_Performance_Report.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();

            addToast("Report generated and downloaded successfully.", "success");
        } catch (error) {
            console.error(error);
            const errorMessage = await extractErrorMessage(error, "Failed to generate report.");
            addToast(errorMessage, "error");
        } finally {
            setTriggering(false);
        }
    };

    const scheduleTime = `${padNumber(schedule.hour)}:${padNumber(schedule.minute)}`;

    const tzOptions = TIMEZONES.map(tz => ({ value: tz, label: tz.replace(/_/g, " ") }));
    const dayOptions = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${getOrdinalSuffix(i + 1)} of the month` }));
    const hourOptions = Array.from({ length: 24 }, (_, i) => ({ value: i, label: padNumber(i) }));
    const minuteOptions = Array.from({ length: 60 }, (_, i) => ({ value: i, label: padNumber(i) }));

    return (
        <div className="ert-container">
            <div className="ert-top-nav">
                <BackButton label="Back to Admin" customClass="ert-back-btn" />
                <button onClick={() => setShowInfoModal(true)} className="ert-info-btn" title="Tool Information">
                    <FaInfoCircle size={20} />
                </button>
            </div>

            <div className="ert-header">
                <div className="ert-header-main">
                    <div className="ert-header-icon">
                        <FaFileExcel />
                    </div>
                    <div className="ert-header-text">
                        <h2 className="ert-title">Employee Performance Report</h2>
                        <p className="ert-subtitle">
                            Generate monthly reports instantly, or automate delivery for your team.
                        </p>
                    </div>
                </div>
                <div className="ert-tabs">
                    {showExecutionTab && (
                        <button
                            className={`ert-tab-btn ${activeTab === 'execution' ? 'active' : ''}`}
                            onClick={() => setActiveTab('execution')}
                        >
                            Execution & Schedule
                        </button>
                    )}
                    {showSettingsTab && (
                        <button
                            className={`ert-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            Report Parameters
                        </button>
                    )}
                </div>
            </div>

            {loadingSchedule ? (
                <div className="ert-loading-skeleton">
                    <div className="skeleton-pulse"></div>
                    <p>Loading configuration...</p>
                </div>
            ) : (
                <>
                    {activeTab === "execution" && (
                        <>
                            <div className="ert-overview-grid">
                                <div className="ert-overview-card">
                                    <span className="ert-overview-label">Target Month</span>
                                    <strong>{currentMonth || "Loading..."}</strong>
                                </div>
                                <div className="ert-overview-card">
                                    <span className="ert-overview-label">Automation</span>
                                    <strong>{schedule.enabled ? "Enabled" : "Disabled"}</strong>
                                </div>
                                <div className="ert-overview-card">
                                    <span className="ert-overview-label">Next Run</span>
                                    <strong>{schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString() : "Not scheduled"}</strong>
                                </div>
                            </div>

                            <div className="ert-main-grid">
                                {canRunReport && (
                                    <div className="ert-section-card ert-ondemand-card">
                                        <div className="ert-section-header">
                                            <h3 className="ert-section-title"><FaRocket className="ert-section-icon highlight-blue" /> Generate Report</h3>
                                        </div>
                                        <div className="ert-section-body">
                                            <p className="ert-description">
                                                Generate the employee performance report for the selected month and download it instantly.
                                            </p>
                                            <button
                                                className={`ert-action-btn primary ${triggering ? 'loading' : ''}`}
                                                onClick={handleTriggerNow}
                                                disabled={triggering}
                                            >
                                                {triggering ? (
                                                    <><div className="ert-spinner" /> Generating Report...</>
                                                ) : (
                                                    <><FaRocket /> Generate Report Now</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {canManageSchedule && (
                                    <div className={`ert-section-card ert-schedule-card ${!schedule.enabled ? 'disabled-mode' : ''}`}>
                                        <div className="ert-section-header split">
                                            <h3 className="ert-section-title"><FaCogs className="ert-section-icon highlight-green" /> Automation Schedule</h3>
                                            <label className="ert-toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={schedule.enabled}
                                                    onChange={(e) => handleScheduleChange("enabled", e.target.checked)}
                                                />
                                                <span className="ert-slider round"></span>
                                                <span className="ert-toggle-label">{schedule.enabled ? "Enabled" : "Disabled"}</span>
                                            </label>
                                        </div>

                                        <div className="ert-section-body">
                                            <p className="ert-description">
                                                Set monthly run timing and recipients for automatic report delivery.
                                            </p>
                                            <div className={`ert-form-grid ${!schedule.enabled ? 'dimmed' : ''}`}>
                                                <div className="ert-form-group full-width">
                                                    <label className="ert-label">Timezone</label>
                                                    <CustomSelect
                                                        options={tzOptions}
                                                        value={schedule.timezone}
                                                        onChange={(val) => handleScheduleChange("timezone", val)}
                                                        disabled={!schedule.enabled}
                                                        icon={FaGlobe}
                                                    />
                                                </div>
                                                <div className="ert-form-group full-width">
                                                    <label className="ert-label">Run Schedule</label>
                                                    <div className="ert-combo-inputs">
                                                        <CustomSelect
                                                            options={dayOptions}
                                                            value={schedule.day_of_month}
                                                            onChange={(val) => handleScheduleChange("day_of_month", val)}
                                                            disabled={!schedule.enabled}
                                                            icon={FaCalendarAlt}
                                                            className="ert-flex-2"
                                                        />
                                                        <span className="ert-separator">at</span>
                                                        <CustomSelect
                                                            options={hourOptions}
                                                            value={schedule.hour}
                                                            onChange={(val) => handleScheduleChange("hour", val)}
                                                            disabled={!schedule.enabled}
                                                            icon={FaClock}
                                                            className="ert-flex-1"
                                                        />
                                                        <span className="ert-time-colon">:</span>
                                                        <CustomSelect
                                                            options={minuteOptions}
                                                            value={schedule.minute}
                                                            onChange={(val) => handleScheduleChange("minute", val)}
                                                            disabled={!schedule.enabled}
                                                            className="ert-flex-1"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="ert-form-group full-width">
                                                    <label className="ert-label">
                                                        <FaEnvelope className="ert-inline-icon" /> Email Recipients
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="ert-text-input"
                                                        placeholder="admin@example.com, hr@example.com"
                                                        value={schedule.email_recipients || ""}
                                                        onChange={(e) => handleScheduleChange("email_recipients", e.target.value)}
                                                        disabled={!schedule.enabled}
                                                    />
                                                    <p className="ert-input-hint">Separate multiple emails with commas.</p>
                                                </div>
                                            </div>

                                            <div className={`ert-schedule-summary ${!schedule.enabled ? 'dimmed' : ''}`}>
                                                <FaClock className="ert-summary-icon" />
                                                <div className="ert-summary-text">
                                                    <strong>{schedule.enabled ? "Active Schedule" : "Schedule Disabled"}</strong>
                                                    <span>
                                                        {schedule.enabled
                                                            ? `Runs monthly on the ${getOrdinalSuffix(schedule.day_of_month)} at ${scheduleTime} (${schedule.timezone})`
                                                            : "Turn on automation to start running on a schedule."}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="ert-action-row">
                                                <button
                                                    className={`ert-action-btn secondary ${saving ? 'loading' : ''}`}
                                                    onClick={handleSaveSchedule}
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <><div className="ert-spinner" /> Saving...</>
                                                    ) : (
                                                        <><FaSave /> Save Schedule</>
                                                    )}
                                                </button>
                                            </div>

                                            {schedule.enabled && schedule.next_run_at && (
                                                <div className="ert-next-run">
                                                    Next run scheduled for: <strong>{new Date(schedule.next_run_at).toLocaleString()}</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {activeTab === "settings" && config && config.parameters && (
                        <div className="ert-settings-container">
                            <div className="ert-section-card ert-params-header">
                                <h3 className="ert-section-title"><FaCogs className="ert-section-icon highlight-purple" /> Parameter Configuration</h3>
                                <p className="ert-description">
                                    Fine-tune the boundaries and baseline rewards for calculating Units Per Transaction (UPT) and Dollars Per Hour (DPH) bonuses. The calculations automatically snap to intervals defined by the "Step Size".
                                </p>
                            </div>

                            <div className="ert-params-grid">
                                {['Main Store Sales Staff', 'Main Store Assistant Managers', 'Outlet Sales Staff'].map(category => (
                                    <div key={category} className="ert-category-card">
                                        <h4 className="ert-category-title"><FaUserTag className="ert-inline-icon highlight-orange" /> {category}</h4>
                                        <p className="ert-category-info">{CATEGORY_INFO[category]}</p>

                                        {['upt', 'dph'].map(metric => (
                                            <div key={metric} className="ert-metric-group">
                                                <h5 className="ert-metric-title">{metric === 'upt' ? 'UPT Bonus Rules' : 'DPH Bonus Rules'}</h5>
                                                <div className="ert-metric-fields">
                                                    <div className="ert-form-group">
                                                        <label className="ert-label">Start Threshold</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="ert-number-input"
                                                            value={config.parameters[category][metric].start_threshold}
                                                            onChange={(e) => handleParameterChange(category, metric, 'start_threshold', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="ert-form-group">
                                                        <label className="ert-label">Base Bonus ($)</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="ert-number-input"
                                                            value={config.parameters[category][metric].base_bonus}
                                                            onChange={(e) => handleParameterChange(category, metric, 'base_bonus', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="ert-form-group">
                                                        <label className="ert-label">Step Size</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="ert-number-input"
                                                            value={config.parameters[category][metric].step_size}
                                                            onChange={(e) => handleParameterChange(category, metric, 'step_size', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="ert-form-group">
                                                        <label className="ert-label">Bonus per Step ($)</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="ert-number-input"
                                                            value={config.parameters[category][metric].bonus_per_step}
                                                            onChange={(e) => handleParameterChange(category, metric, 'bonus_per_step', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <div className="ert-save-bar">
                                <button
                                    className={`ert-action-btn primary ${savingConfig ? 'loading' : ''}`}
                                    onClick={handleSaveConfig}
                                    disabled={savingConfig}
                                >
                                    {savingConfig ? (
                                        <><div className="ert-spinner" /> Saving Parameters...</>
                                    ) : (
                                        <><FaSave /> Save Parameters</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}


            <ToolInfoModal
                isOpen={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                toolName={toolInfo.toolName}
                description={toolInfo.description}
                steps={toolInfo.steps}
                templates={toolInfo.templates}
            />
        </div>
    );
}

export default EmployeeReportTool;
